import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "./Sidebar";
import { FaBarsStaggered } from "react-icons/fa6";
import axios from "axios";
import AdminNav from "./AdminNav";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash, FaDownload, FaShare } from "react-icons/fa";
import Loader from "../../components/Loader";
import "currency-flags/dist/currency-flags.min.css";
import Select from "react-select";
import { currencyOptions } from "./currencyOptions";
import successSound from "../../assets/tune.mp3";
import {
  Modal,
  Button,
  Divider,
  Card,
  Row,
  Col,
  Space,
  Typography,
} from "antd";
import { CheckCircleOutlined, BankOutlined } from "@ant-design/icons";
import html2canvas from "html2canvas";

const { Title, Text } = Typography;

const DepositMoney = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("admintoken");
  const receiptRef = useRef(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState({});
  const [amount, setAmount] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pinCode, setPinCode] = useState("");
  const [error, setError] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isHedgingChecked, setIsHedgingChecked] = useState(false);
  const [hedging, setHedging] = useState("");
  const [sales, setSales] = useState("");
  const [mainTransaction, setMainTransaction] = useState("");

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const togglePasswordVisibility = () =>
    setIsPasswordVisible(!isPasswordVisible);

  const handleCurrencyChange = (option) => {
    setSelectedCurrency(option.value);
  };

  const handleCheckboxSalesChange = (e) => {
    setIsChecked(e.target.checked);
  };

  const handleCheckboxHedgingChange = (e) => {
    setIsHedgingChecked(e.target.checked);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API}/api/v1/auth/get-user/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (data?.success) setUser(data.user);
      } catch (error) {
        console.error(error);
      }
    };
    fetchUserData();
  }, [id]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!amount) return toast.error("Please enter an amount.");
    if (!selectedCurrency) return toast.error("Please select a currency.");
    if (isChecked && !sales) return toast.error("Sales fee is required.");
    if (isHedgingChecked && !hedging)
      return toast.error("Hedging fee is required.");
    setShowConfirmation(true);
  };

  // PIN step removed for now — Confirm submits the deposit directly.
  const handleConfirm = async () => {
    setShowConfirmation(false);
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API}/api/v1/transaction/new`,
        {
          name: user.name,
          email: user.email,
          userId: id,
          amount,
          paymentMethod: "Fiat",
          currency: selectedCurrency,
          asset: "",
          network: "",
          salesChecked: isChecked,
          salesPercentage: isChecked ? sales : 0,
          hedgingChecked: isHedgingChecked,
          hedgingPercentage: isHedgingChecked ? hedging : 0,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success(data.message);
        setShowSuccessModal(true);
        setMainTransaction(data.mainTransaction);
        new Audio(successSound).play();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDeposit = () => {
    setShowSuccessModal(false);
    navigate("/dashboard/admin/deposit");
  };

  const formatOptionLabel = ({ label, flag }) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      <img src={flag} alt="" style={{ width: 20, marginRight: 10 }} />
      <span>{label}</span>
    </div>
  );

  const downloadReceipt = async () => {
    if (receiptRef.current) {
      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `receipt-${mainTransaction._id}.png`;
      link.href = canvas.toDataURL();
      link.click();
    }
  };

  const shareReceipt = async () => {
    if (navigator.share && receiptRef.current) {
      try {
        const canvas = await html2canvas(receiptRef.current, {
          backgroundColor: "#ffffff",
          scale: 2,
          useCORS: true,
        });
        canvas.toBlob(async (blob) => {
          const file = new File([blob], `receipt-${mainTransaction._id}.png`, {
            type: "image/png",
          });
          await navigator.share({
            title: "Transaction Receipt",
            text: `Transaction successful - ${amount} ${selectedCurrency}`,
            files: [file],
          });
        });
      } catch (error) {
        console.error("Error sharing:", error);
        downloadReceipt(); // Fallback to download
      }
    } else {
      downloadReceipt(); // Fallback to download
    }
  };

  const totalAmount = parseFloat(amount || 0);
  const salesFee = isChecked ? (sales / 100) * totalAmount : 0;
  const hedgingFee = isHedgingChecked ? (hedging / 100) * totalAmount : 0;
  const netAmount = totalAmount - salesFee - hedgingFee;

  return (
    <>
      <div className="wrapper d-flex align-items-stretch">
        {loading && <Loader />}
        <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div id="content" className="px-2 pt-3">
          <button type="button" onClick={toggleSidebar} className="bars-btn">
            <FaBarsStaggered />
          </button>
          <AdminNav />

          <div className="shadow-lg rounded-3 p-3 deposit-card">
            <h4 className="text-center mb-3">Admin Deposit </h4>
            <form>
              <div className="mb-3">
                <label>Username</label>
                <input
                  type="text"
                  className="form-control"
                  disabled
                  value={user.name || ""}
                />
              </div>
              <div className="mb-3">
                <label>Email</label>
                <input
                  type="text"
                  className="form-control"
                  disabled
                  value={user.email || ""}
                />
              </div>
              <div className="mb-3">
                <label>Currency</label>
                <Select
                  options={currencyOptions}
                  value={currencyOptions.find(
                    (option) => option.value === selectedCurrency
                  )}
                  onChange={handleCurrencyChange}
                  formatOptionLabel={formatOptionLabel}
                  placeholder="Select Currency"
                  isSearchable
                />
              </div>
              <div className="mb-3">
                <label>Amount</label>
                <input
                  type="number"
                  className="form-control"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </div>
              <div className="d-flex flex-column">
                <div className="d-flex">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxSalesChange}
                  />
                  <label className="form-label ms-3">Sales Charge %</label>
                </div>
                <input
                  type="number"
                  disabled={!isChecked}
                  placeholder="Sales Charge %"
                  value={sales}
                  onChange={(e) => setSales(e.target.value)}
                  className="form-control"
                />
              </div>

              <div className="d-flex flex-column mt-3">
                <div className="d-flex">
                  <input
                    type="checkbox"
                    checked={isHedgingChecked}
                    onChange={handleCheckboxHedgingChange}
                  />
                  <label className="form-label ms-3">Hedging Fee %</label>
                </div>
                <input
                  type="number"
                  disabled={!isHedgingChecked}
                  placeholder="Hedging Fee %"
                  value={hedging}
                  onChange={(e) => setHedging(e.target.value)}
                  className="form-control"
                />
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleFormSubmit}
                  className="btn btn-primary my-3"
                >
                  Deposit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content" style={{ padding: "0px" }}>
              <div className="modal-header">
                <h5 className="modal-title">Confirm Transaction</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirmation(false)}
                ></button>
              </div>
              <div className="modal-body text-start">
                <h6>Name: {user.name}</h6>
                <h6>Email: {user.email}</h6>
                <h6>Payment Method: Fiat</h6>
                <h6>Currency: {selectedCurrency}</h6>
                <h6>
                  Amount: {amount} {selectedCurrency}
                </h6>
                <h6>
                  Sales Fee: {salesFee.toFixed(4)} {selectedCurrency}
                </h6>
                <h6>
                  Hedging Fee: {hedgingFee.toFixed(4)} {selectedCurrency}
                </h6>
                <div className="text-end">
                  <button
                    className="btn btn-secondary mx-2"
                    onClick={() => setShowConfirmation(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary mx-2"
                    onClick={handleConfirm}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Professional Receipt Success Modal */}
      <Modal
        open={showSuccessModal}
        onCancel={handleBackToDeposit}
        footer={null}
        width={800}
        centered
        closable={false}
        style={{ top: 4 }}
      >
        <div
          ref={receiptRef}
          style={{ background: "#ffffff", padding: "12px" }}
        >
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "10px" }}>
            <CheckCircleOutlined
              style={{
                fontSize: "48px",
                color: "#52c41a",
                marginBottom: "6px",
                display: "block",
              }}
            />
            <Title level={3} style={{ color: "#52c41a", margin: "0 0 8px 0" }}>
              Transaction Successful
            </Title>
            <Text type="secondary" style={{ fontSize: "17px" }}>
              Your deposit has been processed successfully
            </Text>
          </div>

          {/* Receipt Card */}
          <Card
            style={{
              background: "linear-gradient(135deg, #1e3351 0%, #1e3352 100%)",
              border: "none",
              borderRadius: "16px",
            }}
          >
            {/* Bank Header */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <BankOutlined
                style={{
                  fontSize: "24px",
                  color: "white",
                  marginBottom: "8px",
                }}
              />
              <Title
                level={4}
                style={{ color: "white", margin: 0, fontSize: "24px" }}
              >
                PAYMENT RECEIPT
              </Title>
              <Text
                style={{ color: "rgba(255,255,255,0.8)", fontSize: "16px" }}
              >
                Transaction ID: {mainTransaction._id}
              </Text>
              <br />
              <Text
                style={{ color: "rgba(255,255,255,0.8)", fontSize: "16px" }}
              >
                {mainTransaction?.createdAt
                  ? new Date(mainTransaction.createdAt).toLocaleString()
                  : ""}
              </Text>
            </div>

            <Divider
              style={{ borderColor: "rgba(255,255,255,0.3)", margin: "16px 0" }}
            />

            {/* Transaction Details */}
            <Row gutter={[0, 12]}>
              <Col span={24}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                    Recipient
                  </Text>
                  <Text strong style={{ color: "white" }}>
                    {user.name}
                  </Text>
                </div>
              </Col>

              <Col span={24}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: "rgba(255,255,255,0.8)" }}>Email</Text>
                  <Text strong style={{ color: "white" }}>
                    {user.email}
                  </Text>
                </div>
              </Col>

              <Col span={24}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                    Payment Method
                  </Text>
                  <Text strong style={{ color: "white" }}>
                    Fiat
                  </Text>
                </div>
              </Col>

              <Col span={24}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                    Currency
                  </Text>
                  <Text strong style={{ color: "white" }}>
                    {selectedCurrency}
                  </Text>
                </div>
              </Col>
            </Row>

            <Divider
              style={{
                borderColor: "rgba(255,255,255,0.3)",
                margin: "20px 0 16px 0",
              }}
            />

            {/* Amount Breakdown */}
            <div
              style={{
                background: "rgba(255,255,255,0.1)",
                padding: "16px",
                borderRadius: "12px",
              }}
            >
              <Row gutter={[0, 8]}>
                <Col span={24}>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                      Deposit Amount
                    </Text>
                    <Text style={{ color: "white" }}>
                      {amount} {selectedCurrency}
                    </Text>
                  </div>
                </Col>

                {salesFee > 0 && (
                  <Col span={24}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                        Sales Fee ({sales}%)
                      </Text>
                      <Text style={{ color: "#ff7875" }}>
                        -{salesFee.toFixed(2)} {selectedCurrency}
                      </Text>
                    </div>
                  </Col>
                )}

                {hedgingFee > 0 && (
                  <Col span={24}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                        Hedging Fee ({hedging}%)
                      </Text>
                      <Text style={{ color: "#ff7875" }}>
                        -{hedgingFee.toFixed(2)} {selectedCurrency}
                      </Text>
                    </div>
                  </Col>
                )}

                <Divider
                  style={{
                    borderColor: "rgba(255,255,255,0.3)",
                    margin: "12px 0 8px 0",
                  }}
                />

                <Col span={24}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text strong style={{ color: "white", fontSize: "16px" }}>
                      Net Amount
                    </Text>
                    <Text strong style={{ color: "#95de64", fontSize: "18px" }}>
                      {netAmount.toFixed(2)} {selectedCurrency}
                    </Text>
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </div>

        {/* Action Buttons */}
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <Space size="middle">
            <Button
              type="primary"
              icon={<FaDownload />}
              onClick={downloadReceipt}
              style={{ borderRadius: "6px" }}
            >
              Download
            </Button>
            <Button
              icon={<FaShare />}
              onClick={shareReceipt}
              style={{ borderRadius: "6px" }}
            >
              Share
            </Button>
            <Button
              onClick={handleBackToDeposit}
              style={{ borderRadius: "6px" }}
            >
              Close
            </Button>
          </Space>
        </div>
      </Modal>
    </>
  );
};

export default DepositMoney;
